import { ComponentType } from "react";
import { IconBaseProps } from "react-icons";
import {
  GiCash,
  GiCrosshair,
  GiGroundbreaker,
  GiHourglass,
  GiPiggyBank,
  GiPistolGun,
  GiPositionMarker,
  GiSkullCrack,
  GiTrophy,
  GiTwoCoins,
} from "react-icons/gi";
import { TbTargetArrow } from "react-icons/tb";
import { FaCrown, FaCommentDots, FaGamepad, FaTrophy } from "react-icons/fa";
import { MdBolt, MdMap } from "react-icons/md";
import { RiSwordFill } from "react-icons/ri";
import { BiTimer } from "react-icons/bi";
import { ImFire } from "react-icons/im";
import { WEAPON_ICON_MAP } from "MVVM/Views/TimelineEditor/constants";

type TClipTag = { key: string; value: string };

export type TTagBadge = {
  label: string;
  icon: ComponentType<IconBaseProps>;
  weight: number;
  featured?: boolean;
  ace?: boolean;
  highlight?: boolean;
  allstar?: boolean;
  /** Daily Top 100 clip; the chip links to /today. */
  top100?: boolean;
  iconUrl?: string;
  tooltip?: string;
};

/**
 * The allstar100 daily cron submits clips through the partner API with
 * metadata `job = allstar100-daily`; clip-create stores it as the
 * `PARTNER_job` clip property, which reaches us as a tag.
 */
export const TOP_100_TAG = { key: "PARTNER_job", value: "allstar100-daily" };

// Loose input type: legacy `clipProperties`-shaped tags have optional fields.
export const isTop100Clip = (
  tags?: { key?: string; value?: string }[] | null,
): boolean =>
  !!tags?.some(
    (t) => t.key === TOP_100_TAG.key && t.value === TOP_100_TAG.value,
  );

const TOP_100_BADGE: TTagBadge = {
  label: "TOP 100",
  icon: FaTrophy,
  weight: 100,
  featured: true,
  top100: true,
  tooltip: "Featured in the Daily Top 100",
};

const WEAPON_DISPLAY_NAMES: Record<string, string> = {
  "desert eagle": "DEAGLE",
  "he grenade": "HE GRENADE",
  "smoke grenade": "SMOKE",
  "incendiary grenade": "INCENDIARY",
  "decoy grenade": "DECOY",
  "dual berettas": "DUALIES",
  "r8 revolver": "R8",
  "zeus x27": "ZEUS",
};

const getWeaponIconUrl = (weapon: string): string => {
  const normalized = weapon.toLowerCase().trim();
  const iconName =
    WEAPON_ICON_MAP[normalized] || normalized.replace(/[\s-]/g, "");
  const { REACT_APP_S3_CLIP_BUCKET } = process.env;
  return `${REACT_APP_S3_CLIP_BUCKET}/static/web/cs2_icons/equipment/${iconName}.svg`;
};

const CS_KILL_COUNT_BADGES: Record<string, TTagBadge> = {
  "5": {
    label: "ACE",
    icon: ImFire,
    weight: 100,
    featured: true,
    ace: true,
    tooltip: "Killed all five players on the enemy team",
  },
  "4": {
    label: "4 KILLS",
    icon: RiSwordFill,
    weight: 90,
    featured: true,
    tooltip: "Killed 4 players in a single round",
  },
  "3": {
    label: "3 KILLS",
    icon: RiSwordFill,
    weight: 80,
    featured: true,
    tooltip: "Killed 3 players in a single round",
  },
};

const CS_SITUATIONAL_BADGES: Record<string, TTagBadge> = {
  quickscope: {
    label: "QUICKSCOPE",
    icon: MdBolt,
    weight: 85,
    featured: true,
    tooltip: "Got a quickscope kill",
  },
  noscope: {
    label: "NOSCOPE",
    icon: MdBolt,
    weight: 85,
    featured: true,
    tooltip: "Got a noscope kill",
  },
  wallbang: {
    label: "WALLBANG",
    icon: GiGroundbreaker,
    weight: 15,
    featured: true,
    tooltip: "Got a kill through a wall",
  },
  "1v1": {
    label: "1V1 CLUTCH",
    icon: FaCrown,
    weight: 75,
    featured: true,
    tooltip: "Won a 1v1 clutch",
  },
  "1v2": {
    label: "1V2 CLUTCH",
    icon: FaCrown,
    weight: 80,
    featured: true,
    tooltip: "Won a 1v2 clutch",
  },
  "1v3": {
    label: "1V3 CLUTCH",
    icon: FaCrown,
    weight: 85,
    featured: true,
    highlight: true,
    tooltip: "Won a 1v3 clutch",
  },
  "1v4": {
    label: "1V4 CLUTCH",
    icon: FaCrown,
    weight: 90,
    featured: true,
    highlight: true,
    tooltip: "Won a 1v4 clutch",
  },
  "1v5": {
    label: "1V5 CLUTCH",
    icon: FaCrown,
    weight: 99,
    featured: true,
    highlight: true,
    ace: true,
    tooltip: "Won a 1v5 clutch",
  },
};

const getCSBadges = (tags: TClipTag[]): TTagBadge[] => {
  const badges: TTagBadge[] = [];
  const tagMap = new Map(tags.map((t) => [t.key, t.value]));

  if (tagMap.get("CS_Command") === "!allstar") {
    badges.push({
      label: "!allstar",
      icon: FaCommentDots,
      weight: 99,
      featured: true,
      allstar: true,
      tooltip: "Made with the !allstar in-game command",
    });
  }

  const killCount = tagMap.get("CS_Kill Count");
  const headshotCount = tagMap.get("CS_Headshots");

  if (killCount) {
    const badge = CS_KILL_COUNT_BADGES[killCount];
    if (badge) badges.push(badge);
  }

  if (headshotCount) {
    const count = parseInt(headshotCount, 10);
    const kills = killCount ? parseInt(killCount, 10) : 0;

    if (count === kills && kills > 1) {
      badges.push({
        label: "ALL HEADSHOTS",
        icon: GiSkullCrack,
        weight: 95,
        featured: true,
        highlight: true,
        tooltip: "Every kill was a headshot",
      });
    } else if (count >= 3) {
      badges.push({
        label: `${count} HEADSHOTS`,
        icon: TbTargetArrow,
        weight: 88,
        featured: true,
        tooltip: `Landed ${count} headshots`,
      });
    }
  }

  // Dedup: same value can appear under `situational` and `clutch` upstream.
  const seenSituational = new Set<string>();
  for (const tag of tags) {
    if (tag.key === "CS_Situational") {
      const normalized = tag.value.toLowerCase();
      if (seenSituational.has(normalized)) continue;
      seenSituational.add(normalized);
      const badge = CS_SITUATIONAL_BADGES[normalized];
      if (badge) badges.push(badge);
    }

    if (tag.key === "CS_Weapons") {
      const normalized = tag.value.toLowerCase().trim();
      const displayName =
        WEAPON_DISPLAY_NAMES[normalized] || tag.value.toUpperCase();
      badges.push({
        label: displayName,
        icon: GiCrosshair,
        weight: 10,
        iconUrl: getWeaponIconUrl(tag.value),
        tooltip: `Used ${displayName}`,
      });
    }
  }

  const map = tagMap.get("CS_Map");
  if (map) {
    badges.push({
      label: map.toUpperCase(),
      icon: MdMap,
      weight: 5,
      tooltip: `Played on ${map}`,
    });
  }

  const round = tagMap.get("CS_Round");
  if (round) {
    badges.push({
      label: `ROUND ${round}`,
      icon: BiTimer,
      weight: 5,
      tooltip: `Round ${round} of the match`,
    });
  }

  // CS_Round Type is multi-value: a clip can have the team's economy
  // (pistol/eco/force-buy/full-buy) AND any of anti-eco/match-point/overtime.
  // Iterate every entry and dedup so each badge only renders once.
  const seenRoundTypes = new Set<string>();
  for (const tag of tags) {
    if (tag.key !== "CS_Round Type") continue;
    const normalized = tag.value.toLowerCase();
    if (seenRoundTypes.has(normalized)) continue;
    seenRoundTypes.add(normalized);
    const badge = CS_ROUND_TYPE_BADGES[normalized];
    if (badge) badges.push(badge);
  }

  return badges;
};

const CS_ROUND_TYPE_BADGES: Record<string, TTagBadge> = {
  pistol: {
    label: "PISTOL ROUND",
    icon: GiPistolGun,
    weight: 12,
    tooltip: "Opening round of the half, pistols only",
  },
  eco: {
    label: "ECO",
    icon: GiPiggyBank,
    weight: 15,
    featured: true,
    tooltip: "Team played on an eco with minimal gear",
  },
  "force-buy": {
    label: "FORCE",
    icon: GiTwoCoins,
    weight: 12,
    tooltip: "Team force-bought with partial gear",
  },
  "full-buy": {
    label: "FULL BUY",
    icon: GiCash,
    weight: 8,
    tooltip: "Team had a full buy",
  },
  "match-point": {
    label: "MATCH POINT",
    icon: GiTrophy,
    weight: 30,
    featured: true,
    highlight: true,
    tooltip: "Round at match point, one win away from clinching",
  },
  overtime: {
    label: "OVERTIME",
    icon: GiHourglass,
    weight: 20,
    featured: true,
    tooltip: "Overtime round",
  },
};

// Display label for a single tag value, matching the badge labels above.
// For filter UIs that list tag values outside of badge rendering.
export const getTagLabel = (key: string, value: string): string => {
  const normalized = value.toLowerCase().trim();
  if (key === "CS_Weapons")
    return WEAPON_DISPLAY_NAMES[normalized] || value.toUpperCase();
  if (key === "CS_Situational")
    return CS_SITUATIONAL_BADGES[normalized]?.label || value.toUpperCase();
  if (key === "CS_Round Type")
    return CS_ROUND_TYPE_BADGES[normalized]?.label || value.toUpperCase();
  // HLTV team and event names keep their own casing (e.g. "Team Vitality").
  if (key === "PARTNER_opponentTeamName" || key === "PARTNER_eventName")
    return value;
  return value.toUpperCase();
};

const LEAGUE_MULTI_KILL_BADGES: Record<string, TTagBadge> = {
  "penta kill": {
    label: "PENTA KILL",
    icon: ImFire,
    weight: 100,
    featured: true,
    ace: true,
    tooltip: "Killed all five players on the enemy team",
  },
  "quadra kill": {
    label: "QUADRA KILL",
    icon: RiSwordFill,
    weight: 95,
    featured: true,
    tooltip: "Killed 4 players in rapid succession",
  },
  "triple kill": {
    label: "TRIPLE KILL",
    icon: RiSwordFill,
    weight: 90,
    featured: true,
    tooltip: "Killed 3 players in rapid succession",
  },
  "double kill": {
    label: "DOUBLE KILL",
    icon: RiSwordFill,
    weight: 80,
    tooltip: "Killed 2 players in rapid succession",
  },
};

const LEAGUE_POSITION_MAP: Record<string, string> = {
  middle: "MID",
  top: "TOP",
  bottom: "BOT",
  utility: "SUPPORT",
  jungle: "JUNGLE",
};

const getLeagueBadges = (tags: TClipTag[]): TTagBadge[] => {
  const badges: TTagBadge[] = [];
  const tagMap = new Map(tags.map((t) => [t.key, t.value]));

  const killCount = tagMap.get("LEAGUE_Kill Count");
  const multiKill = tagMap.get("LEAGUE_Multi Kill");
  const champion = tagMap.get("LEAGUE_Champion");

  if (multiKill) {
    const badge = LEAGUE_MULTI_KILL_BADGES[multiKill.toLowerCase()];
    if (badge) badges.push(badge);
  } else if (killCount === "5") {
    badges.push(LEAGUE_MULTI_KILL_BADGES["penta kill"]);
  } else if (killCount) {
    const count = parseInt(killCount, 10);
    if (count >= 3) {
      badges.push({
        label: `${count} KILLS`,
        icon: RiSwordFill,
        weight: 90,
        featured: true,
        tooltip: `Killed ${count} players`,
      });
    }
  }

  if (champion) {
    badges.push({
      label: champion.toUpperCase(),
      icon: FaGamepad,
      weight: 20,
      tooltip: `Played as ${champion}`,
    });
  }

  const matchType = tagMap.get("LEAGUE_Match Type");
  if (matchType) {
    badges.push({
      label: matchType.toUpperCase(),
      icon: MdMap,
      weight: 5,
      tooltip: `Match type: ${matchType}`,
    });
  }

  const position = tagMap.get("LEAGUE_Position");
  if (position) {
    const roleName =
      LEAGUE_POSITION_MAP[position.toLowerCase()] || position.toUpperCase();
    badges.push({
      label: roleName,
      icon: GiPositionMarker,
      weight: 10,
      tooltip: `Played ${roleName} Lane`,
    });
  }

  return badges;
};

export interface ITagBadgeOptions {
  /**
   * Suppress the TOP 100 chip. The /today page is the Top 100 list itself,
   * so repeating the chip on every row there is pure noise.
   */
  hideTop100?: boolean;
}

export const getTagBadges = (
  tags?: TClipTag[],
  { hideTop100 = false }: ITagBadgeOptions = {},
): TTagBadge[] => {
  if (!tags?.length) return [];

  const hasCS = tags.some((t) => t.key.startsWith("CS_"));
  const hasLeague = tags.some((t) => t.key.startsWith("LEAGUE_"));

  let badges: TTagBadge[] = [];

  if (hasCS) {
    badges = getCSBadges(tags);
  } else if (hasLeague) {
    badges = getLeagueBadges(tags);
  }

  // Game-agnostic: the Top 100 badge rides on a PARTNER_ tag, not a game prefix.
  if (!hideTop100 && isTop100Clip(tags)) badges.push(TOP_100_BADGE);

  // Featured badges sort above plain ones so they win limited-slot rendering.
  // Top 100 outranks everything else so it always survives truncation.
  return badges.sort((a, b) => {
    if (!!a.top100 !== !!b.top100) return a.top100 ? -1 : 1;
    const aEmphasized = !!(a.featured || a.highlight || a.ace);
    const bEmphasized = !!(b.featured || b.highlight || b.ace);
    if (aEmphasized !== bEmphasized) return aEmphasized ? -1 : 1;
    return b.weight - a.weight;
  });
};
